import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Splash } from "@/components/Layout";
import { AdminBookings } from "@/admin/AdminBookings";
import { AdminCustomers } from "@/admin/AdminCustomers";
import { AdminFactors } from "@/admin/AdminFactors";
import { AdminStaff } from "@/admin/AdminStaff";
import { EnvBanner } from "@/components/EnvBanner";
import { AdminMaterials } from "@/admin/AdminMaterials";
import { AdminTeam } from "@/admin/AdminTeam";
import { AdminKyc } from "@/admin/AdminKyc";
import { AdminData } from "@/admin/AdminData";
import { AdminLive } from "@/admin/AdminLive";
import { AdminReferrals } from "@/admin/AdminReferrals";
import { AdminCompliance } from "@/admin/AdminCompliance";
import { AdminRewards } from "@/admin/AdminRewards";
import { AdminForecast } from "@/admin/AdminForecast";
import { AdminPartners } from "@/admin/AdminPartners";
import { AdminRefPrizes } from "@/admin/AdminRefPrizes";
import { AdminOverview } from "@/admin/AdminOverview";
import { AdminMap } from "@/admin/AdminMap";
import { AdminSupport } from "@/admin/AdminSupport";
import { AdminChats } from "@/admin/AdminChats";
import { AdminRatings } from "@/admin/AdminRatings";
import { AdminCommission } from "@/admin/AdminCommission";
import { AdminTargets } from "@/admin/AdminTargets";
import { AdminCustomerTypes } from "@/admin/AdminCustomerTypes";
import { AdminNews } from "@/admin/AdminNews";
import { DetailProvider } from "@/admin/AdminDetail";
import { SidebarBody, Topbar, MobileNav } from "@/admin/AdminShell";
import { sectionsFor } from "@/admin/nav";
import { useLive, useSync, emitSync } from "@/lib/live";

const VIEWS = {
  overview: (p) => <AdminOverview go={p.go} role={p.admin.role} />, map: () => <AdminMap />, live: (p) => <AdminLive role={p.admin.role} />, forecast: () => <AdminForecast />,
  customers: (p) => <AdminCustomers role={p.admin.role} />, bookings: (p) => <AdminBookings role={p.admin.role} />, referrals: () => <AdminReferrals />,
  staff: () => <AdminStaff />, partners: () => <AdminPartners />, kyc: (p) => <AdminKyc role={p.admin.role} />, commission: () => <AdminCommission />, targets: () => <AdminTargets />, ratings: (p) => <AdminRatings role={p.admin.role} />,
  complaints: (p) => <AdminSupport me={p.admin.email} />, chats: () => <AdminChats />, materials: () => <AdminMaterials />, ctypes: () => <AdminCustomerTypes />, factors: () => <AdminFactors />,
  rewards: () => <AdminRewards />, prizes: () => <AdminRefPrizes />, news: () => <AdminNews />, team: (p) => <AdminTeam me={p.admin.email} />,
  data: () => <AdminData />, compliance: () => <AdminCompliance />,
};

const useAlerts = (enabled) => {
  const [a, setA] = useState(null);
  const prev = useRef(null);
  const tick = useCallback(() => enabled && api.get("/admin/alerts").then(({ data }) => {
      const p = prev.current;
      if (p && (data.new_bookings > p.new_bookings || data.awaiting_reply > p.awaiting_reply || data.needs_admin > p.needs_admin)) {
        const msg = data.needs_admin > p.needs_admin ? "No partner accepted a pickup: please assign it" : data.new_bookings > p.new_bookings ? "New pickup booked" : "New complaint message";
        toast.info(msg);
        if ("Notification" in window && Notification.permission === "granted") new Notification("Kindsaver admin", { body: msg, icon: "/icon-192.png" });
      }
      prev.current = data;
      setA(data);
    }).catch(() => {}), [enabled]);
  useEffect(() => {
    if (!enabled) return;
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [enabled, tick]);
  useSync(tick);
  useLive(enabled ? { role: "admin" } : null, (e) => emitSync(e));
  return a;
};

export default function AdminDashboard() {
  const [admin, setAdmin] = useState(null);
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [menu, setMenu] = useState(false);
  useEffect(() => { api.get("/admin/me").then((r) => setAdmin(r.data)).catch(() => setAdmin(false)); }, []);
  const alerts = useAlerts(!!admin);
  if (admin === null) return <Splash />;
  if (!admin) return <Navigate to="/admin/login" replace />;
  const logout = async () => { await api.post("/admin/logout"); nav("/admin/login", { replace: true }); };
  const sections = sectionsFor(admin.role);
  const cur = sections.find((s) => s.key === params.get("s")) || sections[0];
  const go = (k) => { setParams({ s: k }); window.scrollTo({ top: 0 }); };
  const shell = { admin, alerts, cur, go, onLogout: logout };
  return (
    <DetailProvider>
      <div className="flex min-h-screen bg-[#F7F5EF]">
        <aside className="sticky top-0 hidden h-screen w-64 shrink-0 lg:block"><SidebarBody {...shell} /></aside>
        <MobileNav open={menu} setOpen={setMenu} {...shell} />
        <div className="min-w-0 flex-1">
          <EnvBanner />
          <Topbar admin={admin} cur={cur} go={go} sections={sections} openMenu={() => setMenu(true)} />
          <main key={cur.key} className="ks-page p-4 lg:p-8" data-testid={`admin-view-${cur.key}`}>{VIEWS[cur.key]({ admin, go })}</main>
        </div>
      </div>
    </DetailProvider>
  );
}

export const ExportButton = ({ kind, params = {} }) => {
  const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v && v !== "all"))).toString();
  return (
    <a href={`${api.defaults.baseURL}/admin/export/${kind}${qs ? `?${qs}` : ""}`} data-testid={`admin-export-${kind}-btn`}
      className="inline-flex h-10 items-center rounded-xl bg-[#2E9E6B] px-4 text-sm font-bold text-white">Export CSV (Excel)</a>
  );
};
